import { useParams } from 'wouter';
import { AdminMeetings } from '@platform/client';
import { Button, Input } from '@platform/components';
import {
  EIRMeetingUserDetail,
  EIRMeetingOrgDetail,
  EIRMeetingDetail as MeetingDetail,
  EIRMeetingOrgChallenges,
  EIRMeetingOrgProject,
} from '@platform/client/schemas';
import FlexibleLink from '../../../util/FlexibleLink';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { OrganizationLink } from '../organizations/OrganizationsAdmin';

export const EIRMeetingDetail = () => {
  const [updatingNotes, setUpdatingNotes] = useState(false);
  const { meetingId } = useParams();
  const {
    data: meetingData,
    isSuccess,
    isLoading,
    isError,
  } = AdminMeetings.useGetEirMeetingDetail({
    meeting_id: Number(meetingId),
  });

  const queryClient = useQueryClient();
  const updateAttendanceMutator = AdminMeetings.useUpdateAttendance({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: AdminMeetings.getGetEirMeetingDetailQueryKey({
            meeting_id: Number(meetingId),
          }),
        });
      },
    },
  });

  if (!meetingData) {
    return null;
  }

  const { user, organization } = meetingData;

  return (
    <div className='max-w-5xl mx-auto '>
      {isLoading && <div className='text-center'>Loading...</div>}
      {isError && <div className='text-center text-error-500'>Error fetching meeting details</div>}
      {isSuccess && (
        <div className='flex flex-col gap-4 justify-center'>
          <div className='flex justify-between'>
            <Button variant='tertiary-gray' size='sm' href='/'>
              Back to meetings
            </Button>
            <div className='flex gap-2'>
              <Button
                variant={meetingData.attended ? 'tertiary-danger' : 'secondary-color'}
                size='sm'
                onPress={() => {
                  updateAttendanceMutator.mutate({
                    params: {
                      meeting_registrant_id: meetingData.registrationId,
                      attended: !meetingData.attended,
                    },
                  });
                }}
              >
                {meetingData.attended ? 'Mark unattended' : 'Mark attended'}
              </Button>
              {!updatingNotes && (
                <Button variant='tertiary-color' size='sm' onPress={() => setUpdatingNotes(true)}>
                  Update notes
                </Button>
              )}
            </div>
          </div>
          <div>
            <h2 className='font-bold text-lg'>Meeting</h2>
            <div>
              <span className='font-semibold'>Name:</span> {meetingData.title}
            </div>
            <div>
              <span className='font-semibold'>Start Time:</span>{' '}
              {new Date(meetingData.startTime).toLocaleString()}
            </div>
            <div>
              <span className='font-semibold'>Host:</span> {meetingData.zoomHost}
            </div>
            <div>
              <span className='font-semibold'>Join url:</span>{' '}
              <a
                href={meetingData.joinUrl}
                target='_blank'
                className='hover:underline text-brand-500'
              >
                {meetingData.joinUrl}
              </a>
            </div>
            <div>
              <span className='font-semibold'>Invitee Name:</span> {meetingData.registrantName}
            </div>
            <div>
              <span className='font-semibold'>Invitee Email:</span>{' '}
              <a
                className='text-brand-500 hover:underline'
                href={`mailto:${meetingData.registrantEmail}`}
              >
                {meetingData.registrantEmail}
              </a>
            </div>
            <div>
              <span className='font-semibold'>Attended:</span> {meetingData.attended ? 'yes' : 'no'}
            </div>
            {!updatingNotes && (
              <div>
                <span className='font-semibold'>Meeting Notes Link:</span>{' '}
                {meetingData.notes ?
                  <a
                    className='text-brand-500 hover:underline break-words'
                    href={meetingData.notes}
                    target='_blank'
                  >
                    {meetingData.notes}
                  </a>
                : <div>No notes added</div>}
              </div>
            )}
            {updatingNotes && (
              <UpdateNotesInput meetingData={meetingData} setUpdatingNotes={setUpdatingNotes} />
            )}
          </div>
          <div>
            <h2 className='font-bold text-lg'>MovingWorlds Platform</h2>
            {user ?
              <UserInformation userData={user} orgData={organization ?? undefined} />
            : <div>No MW user data available</div>}
          </div>
          <div>
            <h2 className='font-bold text-lg'>Business Challenges</h2>
            {organization?.businessChallenges && organization.businessChallenges.length ?
              <BusinessChallenges businessChallenges={organization.businessChallenges} />
            : <div>No business challenges recorded</div>}
          </div>
          <div>
            <h2 className='font-bold text-lg'>Projects</h2>
            {organization?.projects && organization.projects.length ?
              <Projects projects={organization.projects} />
            : <div>No projects submitted</div>}
          </div>
          <div className='flex flex-col'>
            <h2 className='font-bold text-lg'>Zoho CRM</h2>
            <a
              className='hover:underline text-brand-500'
              href={`https://crm.zoho.com/crm/org667587627/search?searchword=${encodeURIComponent(meetingData.registrantEmail)}`}
              target='_blank'
            >
              {' '}
              Contact search
            </a>
            {organization && (
              <a
                className='hover:underline text-brand-500'
                href={`https://crm.zoho.com/crm/org667587627/search?searchword=${encodeURIComponent(organization.name)}`}
                target='_blank'
              >
                Organization search
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const UserInformation = ({
  userData,
  orgData,
}: {
  userData: EIRMeetingUserDetail;
  orgData?: EIRMeetingOrgDetail;
}) => {
  return (
    <div>
      <div>
        <span className='font-semibold'>User:</span>{' '}
        {userData && orgData ?
          <FlexibleLink
            className='hover:underline text-brand-500'
            type='legacy'
            href={`/organization/${orgData.id}/representative/${userData.id}`}
            newTab={true}
          >
            {userData.firstname} {userData.lastname}
          </FlexibleLink>
        : <span>
            {userData.firstname} {userData.lastname}
          </span>
        }
      </div>
      <div>
        <span className='font-semibold'>Organization:</span>{' '}
        {orgData ?
          <>
            <OrganizationLink organizationId={orgData.id} organizationName={orgData.name} />
            <div>
              {orgData.website && (
                <div>
                  <span className='font-semibold'>Website:</span>{' '}
                  <a
                    href={orgData.website}
                    target='_blank'
                    className='text-brand-500 hover:underline'
                  >
                    {orgData.website}
                  </a>
                </div>
              )}
              <span className='font-semibold'>Registered: </span>
              {new Date(orgData.registrationDate).toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
              })}
            </div>
            <div>
              <span className='font-semibold'>Industries:</span>{' '}
              {orgData.industries.map((industry, index) => (
                <span key={index}>
                  {industry}
                  {index < orgData.industries.length - 1 ? ', ' : ''}
                </span>
              ))}
            </div>
          </>
        : <span>No MW organization data available</span>}
      </div>
    </div>
  );
};

const BusinessChallenges = ({
  businessChallenges,
}: {
  businessChallenges: EIRMeetingOrgChallenges[];
}) => {
  return (
    <div>
      {businessChallenges.map((businessChallenge, index) => (
        <div key={`business-challenge-${index}`} className='mb-2'>
          <span className='text-xs italic'>
            Submitted: {new Date(businessChallenge.createdAt).toLocaleString()}
          </span>
          <div>
            <div className='mb-1'>
              <span className='font-semibold'>Challenge One: </span>
              {businessChallenge.challengeOne} -{' '}
              <span className='italic'>{businessChallenge.contextOne}</span>
            </div>
            <div className='mb-1'>
              <span className='font-semibold'>Challenge Two: </span>
              {businessChallenge.challengeTwo} -{' '}
              <span className='italic'>{businessChallenge.contextTwo}</span>
            </div>
            <div className='mb-1'>
              <span className='font-semibold'>Challenge Three: </span>
              {businessChallenge.challengeThree} -{' '}
              <span className='italic'>{businessChallenge.contextThree}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const Projects = ({ projects }: { projects: EIRMeetingOrgProject[] }) => {
  return (
    <div>
      {projects.length > 0 &&
        projects.map((project, index) => (
          <div key={`project-${index}`} className='mb-2'>
            <span className='text-xs'>[{new Date(project.createdAt).toLocaleDateString()}] - </span>
            <a
              className='hover:underline text-brand-500'
              href={`/platform/admin/projects/review/${project.id}`}
              target='_blank'
            >
              {project.name}
            </a>
          </div>
        ))}
    </div>
  );
};

const UpdateNotesInput = ({
  meetingData,
  setUpdatingNotes,
}: {
  meetingData: MeetingDetail;
  setUpdatingNotes: (value: React.SetStateAction<boolean>) => void;
}) => {
  const [draftNotes, setDraftNotes] = useState<string | undefined>(meetingData.notes ?? undefined);
  const queryClient = useQueryClient();
  const updateNotesMutator = AdminMeetings.useUpdateRegistrantNotes({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: AdminMeetings.getGetEirMeetingDetailQueryKey({
            meeting_id: Number(meetingData.id),
          }),
        });
        setUpdatingNotes(false);
      },
    },
  });
  return (
    <div>
      <Input
        type='multiline'
        value={draftNotes ?? ''}
        onChange={(e) => setDraftNotes(e.target.value)}
      />
      <div className='flex gap-4 justify-end'>
        <Button
          variant='tertiary-danger'
          size='sm'
          onPress={() => {
            setUpdatingNotes(false);
          }}
        >
          Cancel
        </Button>
        <Button
          variant='tertiary-color'
          size='sm'
          onPress={() => {
            updateNotesMutator.mutate({
              data: {
                meetingRegistrantId: meetingData.registrationId,
                notes: draftNotes as string,
              },
            });
          }}
        >
          Save
        </Button>
      </div>
    </div>
  );
};
